import React from 'react';
import { TFunction } from 'react-i18next';
import {
  Link,
  Stack,
  Trans,
  CodeInline,
  OrderedList,
  Text,
  Spinner,
} from '@piwikpro/ui-components';
import FailMessage from '@piwikpro/container-settings-crate/components/FailMessage';
import { OrganizationState } from '@piwikpro/organization-crate';

interface ShopifyInstallProps {
  shopifyContainerSettingsAreLoading: boolean
  shopifyContainerSettingsFailed: boolean
  trackingEndpointPath: any
  trackingDomain: any
  appId: string
  organizationName: string
  license: OrganizationState['details']['license']['current']
  t: TFunction
}

const ShopifyInstall: React.FC<ShopifyInstallProps> = ({
  shopifyContainerSettingsAreLoading,
  shopifyContainerSettingsFailed,
  trackingEndpointPath,
  trackingDomain,
  appId,
  organizationName,
  license,
  t,
}) => {
  const getShopifyLink = () => {
    const SHOPIFY_PIWIK_APP_BASE_URL = 'https://admin.shopify.com/apps/piwik-pro-integration';
    const queryParams = new URLSearchParams({
      piwikProAccountName: organizationName,
      siteOrAppId: appId,
      currentLicensePlan: license?.plan.id || '',
      currentLicenseStatus: license?.status || '',
      trackingEndpoint: `https://${trackingDomain.value}${trackingEndpointPath.value}`,
    });

    return `${SHOPIFY_PIWIK_APP_BASE_URL}?${queryParams.toString()}`;
  };

  return (
    <>
      {/* only container setting check as crucial ones + other parts of url should be loaded/failed-handled earlier */}
      {shopifyContainerSettingsAreLoading && <Spinner />}
      {shopifyContainerSettingsFailed && <FailMessage />}
      {!shopifyContainerSettingsAreLoading && !shopifyContainerSettingsFailed && (
        <OrderedList>
          <Stack spacing="narrow" vertical>
            <Stack.Item>
              {/* as we are not able to assert actual render of Trans, therefore Link href
              we expose link by dataId attribute to be able to test it*/}
              <OrderedList.Item dataId={getShopifyLink()}>
                <Trans
                  i18nKey="apps.details.installation.shopify.line-1"
                  components={[
                    <Link href={getShopifyLink()}>
                      shopify link
                    </Link>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.shopify.line-2"
                  components={[
                    <b>
                      bolded text
                    </b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <Text as="p" size={12} dataId="Caption" color="InkLighter">
                <Trans
                  i18nKey="apps.details.installation.shopify.line-3"
                  components={[
                    <Link size="small" href={getShopifyLink()}>
                      shopify link
                    </Link>,
                  ]}
                />
              </Text>
            </Stack.Item>
            <Stack.Item fill>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.shopify.line-4"
                  components={[
                    <b>
                      bolded text
                    </b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.shopify.line-5"
                  values={{
                    code: trackingEndpointPath.value as string,
                  }}
                  components={[
                    <b>bolded text</b>,
                    <CodeInline dataId="TextStyle">
                      code
                    </CodeInline>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.shopify.line-6')}
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.shopify.line-7"
                  components={[
                    <b>bolded text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
          </Stack>
        </OrderedList>
      )}
    </>
  );
};

export default ShopifyInstall;
